import React from 'react';
import ReactDOM from 'react-dom';
import App from './App';
import reportWebVitals from './reportWebVitals';
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import BugsnagPerformance from '@bugsnag/browser-performance'

const bugsnagApiKey =
  process.env.REACT_APP_BUGSNAG_API_KEY || 'ef1eff6ec9c555df99cf551a5a7f7ed1';
const bugsnagAppVersion = process.env.REACT_APP_VERSION || '2.5.0';

if (bugsnagApiKey) {
  Bugsnag.start({
    apiKey: bugsnagApiKey,
    plugins: [new BugsnagPluginReact()],
    releaseStage: process.env.NODE_ENV,
    enabledReleaseStages: ['production', 'staging'],
    appVersion: bugsnagAppVersion,
    onError: event => {
      addChunkLoadDiagnostics(event, event.originalError);
    },
  })
  BugsnagPerformance.start({ apiKey: bugsnagApiKey })
}

// replace console.* for disable log on production
if (process.env.NODE_ENV === 'production') {
  console.log = () => {};
  console.error = () => {};
  console.debug = () => {};
}
console.log('env', process.env.NODE_ENV)

const CHUNK_LOAD_RELOAD_KEY = 'coachfirst.chunk-load-reloaded';

const isChunkLoadError = error => {
  const message = String(error?.message || error || '');
  return (
    error?.name === 'ChunkLoadError' ||
    /Loading chunk \d+ failed/i.test(message) ||
    /ChunkLoadError/i.test(message)
  );
};

const addChunkLoadDiagnostics = (event, error) => {
  const chunkLoadError = isChunkLoadError(error);

  event.addMetadata('chunkLoad', {
    isChunkLoadError: chunkLoadError,
    errorName: error?.name || null,
    errorMessage: String(error?.message || error || ''),
  });

  if (chunkLoadError) {
    event.context = 'chunk-load-error';
  }
};

const ErrorFallback = ({error}) => {
  const chunkLoadError = isChunkLoadError(error);

  React.useEffect(() => {
    if (!chunkLoadError) return;

    Bugsnag.leaveBreadcrumb('chunk-load-fallback-rendered', {
      recoveryAction: 'reload',
    });

    try {
      if (window.sessionStorage.getItem(CHUNK_LOAD_RELOAD_KEY)) {
        Bugsnag.leaveBreadcrumb('chunk-load-reload-skipped', {
          reason: 'already-reloaded-this-session',
        });
        return;
      }
      window.sessionStorage.setItem(CHUNK_LOAD_RELOAD_KEY, 'true');
    } catch (_) {
      // Ignore storage failures and still attempt the recovery navigation.
      Bugsnag.leaveBreadcrumb('chunk-load-session-storage-unavailable');
    }

    Bugsnag.leaveBreadcrumb('chunk-load-reload-attempted', {
      target: window.location.href,
    });
    window.location.reload();
  }, [chunkLoadError]);

  const message = chunkLoadError
    ? 'A new version of CoachFirst is available. We are reloading the app so the latest payment page can open.'
    : 'Please refresh this page and try again. If the issue continues, contact CoachFirst support.';

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: '#fff',
        color: '#212529',
        textAlign: 'center',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div style={{maxWidth: '520px'}}>
        <h1 style={{fontSize: '24px', marginBottom: '12px'}}>Something went wrong</h1>
        <p style={{fontSize: '16px', lineHeight: 1.5, marginBottom: '20px'}}>
          {message}
        </p>
        <button
          type="button"
          onClick={() => {
            window.location.href = '/';
          }}
          style={{
            border: 0,
            borderRadius: '4px',
            background: '#c72638',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '16px',
            padding: '10px 18px',
          }}
        >
          Go to home page
        </button>
      </div>
    </div>
  );
};

const ErrorBoundary = bugsnagApiKey
  ? Bugsnag.getPlugin('react').createErrorBoundary(React)
  : null;

const app = (
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

ReactDOM.render(
  ErrorBoundary ? (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={event => {
        addChunkLoadDiagnostics(event, event.originalError);

        if (isChunkLoadError(event.originalError)) {
          Bugsnag.leaveBreadcrumb('chunk-load-boundary-caught', {
            errorClass: event.errors?.[0]?.errorClass || null,
          });
        }
      }}
    >
      {app}
    </ErrorBoundary>
  ) : app,
  document.getElementById('root')
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
